import { Box, Typography } from "@mui/material";
import { useBackofficeConstant } from "components/context/ConstantProvider";
import {
  euroReadable,
  formatMonthYear,
  multiple,
  valuationMultiple,
} from "models/backoffice/formatters";
import {
  CaptableChart,
  CaptableFDInfo,
} from "models/backoffice/modelComponents/CaptableWidget";
import useFormatters from "models/backoffice/modelComponents/useFormaters";
import Slide from "models/backoffice/slideDesign/Slide";
import SlideBullets from "models/backoffice/slideDesign/SlideBullets";
import SlideConfigButton from "models/backoffice/slideDesign/SlideConfigButton";
import SlideLabelValue from "models/backoffice/slideDesign/SlideLabelValue";
import SlideSection from "models/backoffice/slideDesign/SlideSection";
import SlideSectionText from "models/backoffice/slideDesign/SlideSectionText";
import SlideSectionTitle from "models/backoffice/slideDesign/SlideSectionTitle";
import SlideSectionWrapper from "models/backoffice/slideDesign/SlideSectionWrapper";
import SlideTitle from "models/backoffice/slideDesign/SlideTitle";
import { forwardRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useConfigurationLinks } from "../DealHome";

const useCustomTranslation = () => {
  const { i18n } = useTranslation("translation");
  const language = i18n.language;

  const tt = (object, key) => {
    if (!object) return object;
    if (language === "en") return object[key] || object[key + "_en"] || null;
    return object[key + "_" + language] || object[key] || null;
  };

  return { tt };
};

const StoredPortfolioSection = ({ stored_portfolio }) => {
  const valuationMethods = useBackofficeConstant("operations").valuationMethods;
  const valuation_method =
    valuationMethods.find((m) => m.value === stored_portfolio?.valuation_method)
      ?.label || stored_portfolio?.valuation_method;
  const { t } = useTranslation("translation");

  if (!stored_portfolio)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100px"
      >
        <Typography variant="body1">
          No stored portfolio data available.
        </Typography>
      </Box>
    );

  return (
    <>
      <SlideLabelValue label={t("Invested")}>
        {euroReadable(stored_portfolio.invested_amount_in_euro)}
      </SlideLabelValue>
      <SlideLabelValue label={t("Realized value")}>
        {euroReadable(stored_portfolio.realized_cash_in_euro)}
      </SlideLabelValue>
      <SlideLabelValue label={t("Unrealized value")}>
        {euroReadable(stored_portfolio.value_in_euro)}
      </SlideLabelValue>
      <SlideLabelValue label={t("Total value")} sx={{ mb: 2 }}>
        {euroReadable(stored_portfolio.total_value_in_euro)}
      </SlideLabelValue>
      <SlideLabelValue label={t("Enterprise value")}>
        {euroReadable(stored_portfolio.deal_enterprise_value_in_euro)}
      </SlideLabelValue>
      <SlideLabelValue label={t("Valuation method")}>
        {t(valuation_method)}
      </SlideLabelValue>
      <SlideLabelValue label={t("Overall multiple")}>
        {multiple(valuationMultiple(stored_portfolio))}
      </SlideLabelValue>
    </>
  );
};

const DealSlide1 = forwardRef(
  ({ dealVisualizationInformation, scaleActive }, ref) => {
    const configurationLinks = useConfigurationLinks(
      dealVisualizationInformation,
    );

    const {
      deal,
      deal_fund,
      deal_information,
      deal_reporting,
      deal_exit,
      fund,
      captable,
      stored_portfolio,
      valuation_period,
      initial_operation,
    } = dealVisualizationInformation || {};
    const { t } = useTranslation("translation");
    const { tt } = useCustomTranslation();
    const { formatDate, formatPercentage } = useFormatters();

    return (
      <Slide
        slideWidth={1000}
        scaleActive={scaleActive}
        loading={!dealVisualizationInformation}
      >
        {dealVisualizationInformation && (
          <>
            <SlideConfigButton configurationLinks={configurationLinks} />
            <SlideTitle size={2}>
              {deal?.logo?.url && (
                <img
                  key={deal?.logo?.url}
                  src={deal?.logo?.url}
                  alt="Logo"
                  style={{
                    maxWidth: 100,
                    maxHeight: 21.83,
                    width: "auto",
                    height: "auto",
                    display: "block",
                    backgroundColor: "transparent",
                  }}
                />
              )}
            </SlideTitle>
            <SlideSectionWrapper ref={ref}>
              <SlideSectionTitle>{t("The company")}</SlideSectionTitle>
              <SlideSectionText>
                <SlideBullets
                  value={tt(deal_information, "company_description")}
                />
              </SlideSectionText>
              <SlideSectionTitle>{t("Deal overview")}</SlideSectionTitle>
              <SlideSectionText size={4} borderRight>
                <Typography variant="h3">{t("Key terms at entry")}</Typography>
                <SlideLabelValue label={t("Initial investment date")}>
                  {formatMonthYear(deal.date)}
                </SlideLabelValue>
                <SlideLabelValue label={t("Instrument")}>
                  {deal_information?.instrument}
                </SlideLabelValue>
                <SlideLabelValue label={t("Type of investment")}>
                  {`${t(deal.round_type)}, ${t(
                    deal_information?.minority_majority,
                  )}`}
                </SlideLabelValue>
                <SlideLabelValue label={t("Ring team")}>
                  {tt(deal_information, "ring_team_description")}
                </SlideLabelValue>
                <SlideLabelValue label={t("Ring role")}>
                  {t(deal_information?.ring_lead_investor ? "Lead" : "Follow")}
                </SlideLabelValue>
                <SlideLabelValue label={t("Financing round")}>
                  {euroReadable(deal_information?.entry_round_size_in_euro)}
                </SlideLabelValue>
                <SlideLabelValue
                  label={t("Co-investors")}
                  isMissing={!deal_information?.co_investors}
                >
                  {deal_information?.co_investors}
                </SlideLabelValue>
                {fund.display_entry_multiple && (
                  <SlideLabelValue
                    label={t("Entry multiple")}
                    isMissing={!deal_information?.entry_multiple}
                  >
                    {deal_information?.entry_multiple}
                  </SlideLabelValue>
                )}
                {fund.display_equity_pre_valuation && (
                  <SlideLabelValue
                    label={t("Entry equity valuation (pre)")}
                    isMissing={
                      !initial_operation?.pre_money_equity_valuation_in_euro
                    }
                    labelTooltip="To be filled in the first operation on this deal."
                  >
                    {euroReadable(
                      initial_operation?.pre_money_equity_valuation_in_euro,
                    )}
                  </SlideLabelValue>
                )}
                {fund.display_expected_exit_multiple && (
                  <SlideLabelValue
                    label={t("Expected exit multiple")}
                    isMissing={!deal_exit?.expected_exit_multiple_description}
                  >
                    {deal_exit?.expected_exit_multiple_description}
                  </SlideLabelValue>
                )}
                {fund.display_expected_type_of_exit && (
                  <SlideLabelValue
                    label={t("Expected type of exit")}
                    isMissing={!deal_exit?.expected_type_of_exit}
                  >
                    {deal_exit?.expected_type_of_exit}
                  </SlideLabelValue>
                )}
              </SlideSectionText>
              <SlideSectionText size={4} borderRight>
                <Typography variant="h3">
                  {t("Deal Overview as of")} {formatDate(valuation_period.date)}
                </Typography>
                <StoredPortfolioSection stored_portfolio={stored_portfolio} />
              </SlideSectionText>
              <SlideSectionText size={4}>
                <Typography variant="h3">
                  {t("Capitalization table (non fully-diluted)")}
                </Typography>
                <CaptableChart captable={captable} fundId={fund.id} />
                <Typography variant="h3" marginTop={2} marginBottom={0}>
                  {t("Fully diluted information")}
                </Typography>
                <CaptableFDInfo captable={captable} fundId={fund.id} />
              </SlideSectionText>
              <SlideSection size={4} borderRight>
                <SlideSectionTitle>
                  {t("Investment rationale")}
                </SlideSectionTitle>
                <SlideSectionText>
                  <SlideBullets
                    value={tt(deal_information, "investment_rationale")}
                  />
                </SlideSectionText>
              </SlideSection>
              <SlideSection size={4} borderRight>
                <SlideSectionTitle>{t("Risks")}</SlideSectionTitle>
                <SlideSectionText>
                  <SlideBullets value={tt(deal_information, "risks")} />
                </SlideSectionText>
              </SlideSection>
              <SlideSection size={4}>
                <SlideSectionTitle>
                  {t("Value creation plan")}
                </SlideSectionTitle>
                <SlideSectionText>
                  <SlideBullets
                    value={tt(deal_information, "value_creation_plan")}
                  />
                </SlideSectionText>
              </SlideSection>
            </SlideSectionWrapper>
          </>
        )}
      </Slide>
    );
  },
);

export default DealSlide1;
